/**
 * Bongo — RSI(8/14/19) ladder + close-vs-SMA(9), latched. This is the CHART's
 * logic (the canonical version): same `rsi`/`sma` from `@/lib/ta` and the same
 * conditions as `customMda.ts` `computeSignals` (daily) and `bongoHtfState`
 * (weekly/monthly via lookahead_off). One version — the chart's
 * Bongo D·W·M study consumes these helpers rather than a second copy.
 *
 *   buy  = rsi8 >= rsi14 >= rsi19  AND  close >= sma9
 *   sell = rsi8  < rsi14  < rsi19  AND  close  < sma9
 *   state latches: +1 bull until a sell, -1 bear until a buy, 0 before either.
 *
 * Weekly/monthly follow the AFIAT BONGO Band script bar-for-bar: each chart
 * bar reads `request.security(…, "1W"/"1M", close/rsi/sma, lookahead_off)` and
 * evaluates buy/sell on THOSE HTF values (`mycloseHT2 >= myAvg9_HT2`, not the
 * daily close). The `var` latch lives on the chart bar, same as the Pine
 * `if Bongo_HT2_Buy` / `if Bongo_HT2_Sell` blocks. Identical computation
 * to the chart's weekly row (`bongoHtfState`), which is not optional — the
 * scanner surfaces `lastFinite(…)`, i.e. exactly the bar the chart draws at its
 * right edge, so any divergence would put the table and the chart in direct
 * contradiction.
 *
 * The forming-week edge means the LATEST value can change once the week closes.
 * That is inherent to matching TradingView's realtime bar and is why the chart
 * study is pinned in `lib/chart/repaintingStudies.ts`.
 */
import { familyGroup } from "../indicatorGroups";
import { TF_DWM, TF_PARAM } from "./timeframe";
import { rsi, sma } from "@/lib/ta";
import type { OHLCV, IndicatorParams, SharedParam, SharedIndicatorDef } from "../types";
import { ageSeries } from "./age";
import { bucketKey, resampleByLastClose, lastClosedHtIndexPerDay } from "./weekly";

const len = (v: number | undefined, d: number) =>
  Math.max(1, Math.round(typeof v === "number" && Number.isFinite(v) ? v : d));

/** Configurable Bongo inputs — the RSI ladder lengths + the close-vs-SMA length.
 *  Defaults 8/14/19/9 reproduce the canonical Bongo (and the chart study). */
export const BONGO_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "rsiFast", label: "RSI fast", kind: "number", default: 8, min: 2, max: 100, step: 1 },
  { id: "rsiMid", label: "RSI mid", kind: "number", default: 14, min: 2, max: 100, step: 1 },
  { id: "rsiSlow", label: "RSI slow", kind: "number", default: 19, min: 2, max: 100, step: 1 },
  { id: "smaLen", label: "SMA length", kind: "number", default: 9, min: 1, max: 200, step: 1 },
];

/** Resolve the four Bongo lengths from a params object (defaults preserve the
 *  canonical 8/14/19/9). */
function bongoLens(p: IndicatorParams = {}) {
  return {
    fast: len(p.rsiFast, 8),
    mid: len(p.rsiMid, 14),
    slow: len(p.rsiSlow, 19),
    sma: len(p.smaLen, 9),
  };
}

/** Per-bar raw regime (+1/-1/0) on one close series — RSI ladder and the
 *  close-vs-SMA9 leg are the same bars. Daily Bongo latches this on the chart
 *  closes; weekly/monthly latch the same predicate on each chart bar using
 *  the mapped HTF close (`mycloseHT2` / `mycloseHT3`). */
function rawRegimeOnCloses(closes: number[], p: IndicatorParams = {}): (-1 | 0 | 1)[] {
  const L = bongoLadder(closes, p);
  return L.buy.map((b, i) => (b ? 1 : L.sell[i] ? -1 : 0)) as (-1 | 0 | 1)[];
}

/** Latch a raw regime into a held state: once +1/-1 fires it persists until the
 *  opposite fires (Pine `var` latching). 0 before the first signal. Exported for
 *  `./timing.ts`, which latches the same way on a stricter predicate. */
export function latchState(regime: ReadonlyArray<-1 | 0 | 1>): number[] {
  const out: number[] = new Array(regime.length).fill(0);
  let last = 0;
  for (let i = 0; i < regime.length; i++) {
    if (regime[i] === 1) last = 1;
    else if (regime[i] === -1) last = -1;
    out[i] = last;
  }
  return out;
}

/** The Bongo RSI ladder + SMA9 per-bar predicates on a close series. The single
 *  source for the daily Bongo regime — `customMda.ts` `computeSignals` consumes
 *  this too (one version). Returns the raw RSI/SMA arrays plus the per-bar
 *  buy/sell and rsiUp/rsiDn booleans (the latter for the chart's Timing Band,
 *  which layers a WMA(30) on the same ladder). */
export function bongoLadder(closes: number[], p: IndicatorParams = {}): {
  rsi8: (number | null)[];
  rsi14: (number | null)[];
  rsi19: (number | null)[];
  sma9: (number | null)[];
  buy: boolean[];
  sell: boolean[];
  rsiUp: boolean[];
  rsiDn: boolean[];
} {
  const n = closes.length;
  const { fast, mid, slow, sma: smaLen } = bongoLens(p);
  const r8 = rsi(closes, fast);
  const r14 = rsi(closes, mid);
  const r19 = rsi(closes, slow);
  const m9 = sma(closes, smaLen);
  const buy = new Array<boolean>(n).fill(false);
  const sell = new Array<boolean>(n).fill(false);
  const rsiUp = new Array<boolean>(n).fill(false);
  const rsiDn = new Array<boolean>(n).fill(false);
  for (let i = 0; i < n; i++) {
    const a = r8[i], b = r14[i], c = r19[i], m = m9[i], cl = closes[i]!;
    if (a == null || b == null || c == null || m == null || !Number.isFinite(cl)) continue;
    const up = a >= b && b >= c;
    const dn = a < b && b < c;
    rsiUp[i] = up;
    rsiDn[i] = dn;
    if (up && cl >= m) buy[i] = true;
    if (dn && cl < m) sell[i] = true;
  }
  return { rsi8: r8, rsi14: r14, rsi19: r19, sma9: m9, buy, sell, rsiUp, rsiDn };
}

/** Daily Bongo latched state (+1/-1/0), index-aligned to closes. */
export function bongoDailyState(data: OHLCV, p: IndicatorParams = {}): number[] {
  return latchState(rawRegimeOnCloses(data.closes, p));
}

/** Weekly Bongo latched state — AFIAT `Bongo_HT2_*`: last CLOSED week on
 *  history, forming week on the newest bar, weekly close vs weekly SMA. */
export function bongoWeeklyState(data: OHLCV, p: IndicatorParams = {}): (number | null)[] {
  const { closes, times } = data;
  const n = closes.length;
  if (!times || times.length !== n) return new Array(n).fill(null);
  return bongoHtfState(times, closes, "week", p);
}

/**
 * Wilder RMA / RSI periods (seed included) to drop remaining seed error
 * below `target`. Same arithmetic `htfdepth:check` pins by hand — RSI(19)
 * to 1% is 105 periods. A framed 3M window plus the chart warmup pad is
 * ~28 monthly buckets, which is why a first paint used to latch the wrong
 * colour until the user loaded more history.
 */
export function wilderPeriodsToConverge(len: number, target = 0.01): number {
  const L = Math.max(2, Math.round(len));
  return L + Math.ceil(Math.log(target) / Math.log((L - 1) / L));
}

/** HT periods the Bongo RSI ladder needs before a latched colour is real. */
export function bongoHtfMinPeriods(p: IndicatorParams = {}): number {
  return wilderPeriodsToConverge(bongoLens(p).slow, 0.01);
}

/**
 * Weekly or monthly Bongo on a CHART's daily grid, with optional deeper history
 * for the ladder — same as TradingView loading the whole symbol.
 *
 * Matches the AFIAT script's per-bar `if`: on each chart bar, read the HTF
 * RSI/SMA/close (`request.security` / `lastClosedHtIndexPerDay`) and latch
 * buy/sell there. The RSI/SMA are recursive, so pass `deep` (the 23y daily
 * store) or a 3M window will recolour as you pan — that is data, not a
 * different predicate. There is no extra "wait for 105 HT bars" gate; Pine
 * latches as soon as `ta.rsi` / `ta.sma` are not `na`.
 */
export function bongoHtfState(
  chartTimes: ReadonlyArray<number>,
  chartCloses: ReadonlyArray<number>,
  freq: "week" | "month",
  p: IndicatorParams = {},
  deep?: { times: ReadonlyArray<number>; closes: ReadonlyArray<number> } | null,
): number[] {
  const n = chartCloses.length;
  if (chartTimes.length !== n || n === 0) return new Array(n).fill(0);
  const useDeep = !!(deep && deep.times.length > n && deep.closes.length === deep.times.length);
  const srcT = useDeep ? deep!.times : chartTimes;
  const srcC = useDeep ? deep!.closes : chartCloses;
  const { htTimes, htCloses } = resampleByLastClose(srcT, srcC, freq);
  const L = bongoLadder(htCloses, p);
  const idx = lastClosedHtIndexPerDay(chartTimes, htTimes);
  const out: number[] = new Array(n).fill(0);
  let last = 0;
  for (let i = 0; i < n; i++) {
    const k = idx[i]!;
    if (k >= 0) {
      if (L.buy[k]) last = 1;
      else if (L.sell[k]) last = -1;
    }
    out[i] = last;
  }
  return out;
}

/** Monthly twin of {@link bongoWeeklyState} — AFIAT `Bongo_HT3_*`: last CLOSED
 *  month on history, forming month on the newest bar, monthly close vs SMA.
 *  Pass `deep` (scanner `1M` / chart `htfBarStore`) so RSI(19) matches TV's
 *  full-history ladder on a 760-day window. */
export function bongoMonthlyState(
  data: OHLCV,
  p: IndicatorParams = {},
  deep?: { times: ReadonlyArray<number>; closes: ReadonlyArray<number> } | null,
): number[] {
  const { closes, times } = data;
  const n = closes.length;
  if (!times || times.length !== n) return new Array(n).fill(0);
  return bongoHtfState(times, closes, "month", p, deep);
}

/** 0-based period ordinal per daily bar — increments on every week/month
 *  boundary, so `ordinal[i] - ordinal[j]` is the number of periods between. */
function periodOrdinals(times: ReadonlyArray<number>, freq: "week" | "month"): number[] {
  const out: number[] = new Array(times.length).fill(0);
  let key: string | null = null;
  let ord = -1;
  for (let i = 0; i < times.length; i++) {
    const k = bucketKey(times[i]!, freq);
    if (k !== key) {
      key = k;
      ord++;
    }
    out[i] = ord;
  }
  return out;
}

/** Age of the current latched HTF state, in HTF periods (weeks or months). */
function bongoHtfAge(
  data: OHLCV,
  state: ReadonlyArray<number | null>,
  freq: "week" | "month",
): (number | null)[] {
  const { times } = data;
  const n = state.length;
  if (!times || times.length !== n) return new Array(n).fill(null);
  const ord = periodOrdinals(times, freq);
  const out: (number | null)[] = new Array(n).fill(null);
  let prev: number | undefined;
  let start = 0;
  for (let i = 0; i < n; i++) {
    const v = state[i];
    if (v == null || v === 0) {
      out[i] = null;
      continue;
    }
    if (prev === undefined || v !== prev) {
      prev = v;
      start = ord[i]!;
    }
    out[i] = ord[i]! - start + 1;
  }
  return out;
}

/** Weekly Bongo age measured in WEEKLY bars (not daily) — how many ISO weeks the
 *  CURRENT weekly state has spanned, counting the week it began as 1.
 *
 *  The state is the weekly bar's own Bongo, projected onto the daily grid, so
 *  the `Rockets` preset's `bongo_w_age > 3` still means "more than three weeks".
 *
 *  Inherits the state's forming-week edge: when the forming week disagrees with
 *  the last completed one, the latest bar reads as a fresh flip and the age
 *  resets to 1, then returns once the week closes. That follows from tracking
 *  the state it describes — an age that stayed at 10 while the state next to it
 *  said "turned today" would be the worse answer. */
export function bongoWeeklyAge(data: OHLCV, p: IndicatorParams = {}): (number | null)[] {
  return bongoHtfAge(data, bongoWeeklyState(data, p), "week");
}

/** Monthly Bongo age in MONTHS, same forming-period edge as the monthly state. */
export function bongoMonthlyAge(
  data: OHLCV,
  p: IndicatorParams = {},
  deep?: { times: ReadonlyArray<number>; closes: ReadonlyArray<number> } | null,
): (number | null)[] {
  return bongoHtfAge(data, bongoMonthlyState(data, p, deep), "month");
}

const ageOfState = (state: (number | null)[]): (number | null)[] => {
  const age = ageSeries(state, "state");
  for (let i = 0; i < state.length; i++) if (!state[i]) age[i] = null; // null when neutral
  return age;
};

const STATE_LABELS = { pos: "Buy", neg: "Sell", zero: "—" } as const;

// One def per concept with a Timeframe select (default Daily). The default-param
// column reads the precomputed daily field; Weekly / Monthly read bongo_w_* /
// bongo_m_* instantly via the tf-resolver in dynamicIndicatorColumns.
export const BONGO_SIGNAL: SharedIndicatorDef = {
  id: "mpp-bongo-signal",
  label: "Afiat - Bongo Ribbon",
  group: familyGroup("bongo"),
  kind: "state",
  format: "state",
  stateLabels: STATE_LABELS,
  panelKey: "bongo_d_state",
  // Chart Bongo reads the forming bar; without this the scanner Last ticks
  // on the live close while Daily/Weekly/Monthly stay on the last worker bake.
  liveOverlay: true,
  params: [TF_DWM, ...BONGO_PARAMS],
  computeSeries: (data, p) =>
    p[TF_PARAM] === 2 ? bongoMonthlyState(data, p)
    : p[TF_PARAM] === 1 ? bongoWeeklyState(data, p)
    : bongoDailyState(data, p),
};

export const BONGO_AGE: SharedIndicatorDef = {
  id: "mpp-bongo-age",
  label: "Afiat - Bongo Ribbon Age",
  group: familyGroup("bongo"),
  kind: "age",
  format: "age",
  panelKey: "bongo_d_age",
  liveOverlay: true,
  params: [TF_DWM, ...BONGO_PARAMS],
  computeSeries: (data, p) =>
    p[TF_PARAM] === 2 ? bongoMonthlyAge(data, p)
    : p[TF_PARAM] === 1 ? bongoWeeklyAge(data, p)
    : ageOfState(bongoDailyState(data, p)),
};

export const BONGO_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [BONGO_SIGNAL, BONGO_AGE];
